import { Badge, ButtonV2, Checkbox, Dialog, Modal, Search, Spinner, TooltipV2 } from '@cimplico/components';
import { useRef, useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useFileRecords } from '../../queries/use-file-records';
import { useUpdateFileRecords } from '../../queries/use-update-file-records';
import { FileRecordNew, FileRecordTypeEnum } from '../../types/file-record';
import { motion } from 'framer-motion';
import { isFolderType } from './file-utils';
import FilesBreadcrumbs from './FilesBreadcrumbs';
import RecordTypeIcon from './RecordTypeIcon';
import NoResults from '../../components/empty-state/NoResults';
import ErrorSplashScreen from '../../pages/ErrorSplashScreen';
import FyiLogo from '../../assets/fyi.png';

type MoveFileWindowProps = {
    initialFolderId?: string;
    workpaperId?: string;
    fileRecords: FileRecordNew[];
    onClose: () => void;
};

function MoveFileWindow({ initialFolderId, workpaperId, fileRecords, onClose }: MoveFileWindowProps): JSX.Element {
    const [currentFolderId, setCurrentFolderId] = useState(initialFolderId);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
    const [search, setSearch] = useState('');
    const [showProviderConfirm, setShowProviderConfirm] = useState(false);
    const [providerReferences, setProviderReferences] = useState(false);

    const searchRef = useRef<HTMLInputElement | undefined>();

    const fileRecordsQuery = useFileRecords(page, limit, workpaperId, currentFolderId, '', 'sort[dir]=asc&sort[field]=name', search);
    const updateFileRecordsQuery = useUpdateFileRecords();

    function handleLevelSelect(value: string): void {
        if (searchRef.current) {
            searchRef.current.value = '';
        }
        setCurrentFolderId(value);
        setPage(1);
        setSearch('');
    }

    function handleRecordClick(record: FileRecordNew): void {
        if (searchRef.current) {
            searchRef.current.value = '';
        }
        if (isFolderType(record.recordType)) {
            setCurrentFolderId(record.id);
            setSearch('');
        }
    }

    function moveFiles(providerAction?: string): void {
        const fyiRecords = fileRecords.filter((fr) => fr.recordType === FileRecordTypeEnum.FYI_DOCUMENT);

        const movePath = fileRecordsQuery.data?.data.path;

        const movingUp = !movePath?.find((p) => p.id === workpaperId) && fileRecords.some((fr) => fr.metadata?.workpaperId === workpaperId);

        if (movingUp && fyiRecords.length > 0 && !providerAction) {
            setShowProviderConfirm(true);
            return;
        }

        updateFileRecordsQuery.mutate(
            fileRecords.map((record) => {
                return { id: record.id, fields: { parentFolderId: currentFolderId }, providerAction };
            }),
            {
                onSettled: () => {
                    onClose();
                    toastNotification('success', `${fileRecords.length} file ${fileRecords.length === 1 ? 'record' : 'records'} moved!`);
                },
            }
        );
    }

    if (showProviderConfirm) {
        return (
            <Dialog
                title="FYI Document Action Required"
                description="FYI documents cannot be moved out of the workpaper. Original copies will be made."
                onClose={(): void => setShowProviderConfirm(false)}
                onSecondaryActionClick={(): void => setShowProviderConfirm(false)}
                onPrimaryActionClick={(): void => {
                    moveFiles(providerReferences ? 'REFERENCE' : 'ORIGINAL');
                    setShowProviderConfirm(false);
                }}
                isOpen
                type="info"
                primaryAction="Continue"
                secondaryAction="Cancel"
            >
                <div className="pb-4">
                    <Checkbox
                        label="Make references instead."
                        checked={providerReferences}
                        onChange={(e): void => setProviderReferences(Boolean(e.target.value))}
                    />
                </div>
            </Dialog>
        );
    }

    return (
        <Modal isOpen={true} onClose={onClose}>
            <motion.div
                className="relative"
                initial={false}
                transition={{ duration: 0.2 }}
                animate={{
                    minHeight: 450,
                    maxWidth: 1200,
                    height: fileRecordsQuery.isLoading ? 450 : 750,
                    width: fileRecordsQuery.isLoading ? 450 : '80vw',
                }}
            >
                {(fileRecordsQuery.isLoading || updateFileRecordsQuery.isLoading) && (
                    <div className="flex items-center justify-center h-full">
                        <Spinner />
                    </div>
                )}
                {fileRecordsQuery.isError && (
                    <ErrorSplashScreen
                        title="An error has occurred."
                        leftArrow={true}
                        redirectTitle="Back to files"
                        handleClick={onClose}
                    />
                )}
                {fileRecordsQuery.isSuccess && (
                    <motion.div
                        className="flex flex-col h-full"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0, delay: fileRecordsQuery.isFetching ? 0 : 0.2 }}
                        animate={{ opacity: 1 }}
                    >
                        <div className="px-8 py-6 flex flex-1 flex-col h-[400px]">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center [&>*]:border-b-0 [&>*]:p-0 border-b pb-1 gap-4">
                                {search || !fileRecordsQuery.data.data.path ? (
                                    <p className="text-sm text-gray-600">Searching in all folders...</p>
                                ) : (
                                    <div className="overflow-auto whitespace-nowrap w-full [&>*]:border-b-0 [&>*]:p-0">
                                        <FilesBreadcrumbs onLevelSelect={handleLevelSelect} path={fileRecordsQuery.data.data.path} />
                                    </div>
                                )}
                                <div className="justify-self-end w-full max-w-[280px]">
                                    <Search
                                        placeholder="Search here and sub-folders..."
                                        onChange={setSearch}
                                        defaultValue={search}
                                        inputRef={searchRef}
                                    />
                                </div>
                            </div>
                            <div className="pt-4 overflow-auto flex flex-col gap-2">
                                {fileRecordsQuery.data.data.records.length === 0 && (
                                    <>
                                        {!search ? (
                                            <p className="text-gray-500 text-sm text-center mt-4">No file records.</p>
                                        ) : (
                                            <NoResults search={search} />
                                        )}
                                    </>
                                )}
                                {fileRecordsQuery.data.data.records.map((record) => {
                                    return (
                                        <div key={record.id}>
                                            {isFolderType(record.recordType) ? (
                                                <FolderRow
                                                    showPath={Boolean(search)}
                                                    record={record}
                                                    onFolderClick={handleRecordClick}
                                                    onBreadCrumbsClick={handleLevelSelect}
                                                />
                                            ) : (
                                                <OtherFileRow
                                                    record={record}
                                                    showPath={Boolean(search)}
                                                    onBreadCrumbsClick={handleLevelSelect}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="bg-gray-50 py-3 flex flex-row items-center justify-end gap-2 px-4 rounded-b-md">
                            <ButtonV2
                                size="xs"
                                type="hollow"
                                label="Cancel"
                                onClick={onClose}
                                isDisabled={updateFileRecordsQuery.isLoading}
                            />
                            <TooltipV2
                                message="Can't move files to the same folder."
                                disabled={currentFolderId !== initialFolderId}
                                position="left"
                            >
                                <ButtonV2
                                    size="xs"
                                    type="secondary"
                                    label="Move here"
                                    onClick={moveFiles}
                                    isDisabled={currentFolderId === initialFolderId || updateFileRecordsQuery.isLoading}
                                />
                            </TooltipV2>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </Modal>
    );
}

type FolderRowProps = {
    showPath?: boolean;
    record: FileRecordNew;
    onFolderClick: (record: FileRecordNew) => void;
    onBreadCrumbsClick: (level: string) => void;
};
function FolderRow({ record, onFolderClick, onBreadCrumbsClick, showPath = false }: FolderRowProps): JSX.Element {
    const disabled = record.provider === 'FYI' || record.locked;

    return (
        <div key={record.id} className={`flex items-center gap-4 pl-2`}>
            <div className={disabled ? 'opacity-[0.65]' : ''}>
                <RecordTypeIcon recordType={record.recordType} system={record.systemRecord} />
            </div>

            <div>
                {showPath && (
                    <div>
                        <FilesBreadcrumbs
                            size="small"
                            onLevelSelect={onBreadCrumbsClick}
                            path={record.path.slice(0, record.path.length - 1)}
                        />
                    </div>
                )}
                <TooltipV2 disabled={!disabled} message={'Cannot move files into locked or integration folders.'}>
                    <div className="flex gap-2">
                        <p
                            className={`text-left text-sm font-medium cursor-pointer ${
                                disabled ? 'opacity-[0.65] pointer-events-none text-gray-600' : 'text-indigo-600'
                            }`}
                            onClick={disabled ? undefined : (): void => onFolderClick(record)}
                        >
                            {record.name}
                        </p>
                        {record.provider === 'FYI' && <img src={FyiLogo} className="w-6 h-6 shrink-0" />}
                    </div>
                </TooltipV2>
            </div>
        </div>
    );
}

type OtherFileRowProps = {
    record: FileRecordNew;
    showPath?: boolean;
    onBreadCrumbsClick: (level: string) => void;
};

function OtherFileRow({ record, showPath, onBreadCrumbsClick }: OtherFileRowProps): JSX.Element {
    return (
        <div key={record.id} className="flex items-center gap-4 h-8 pl-2 opacity-[0.65] text-gray-600">
            <RecordTypeIcon recordType={record.recordType} system={record.systemRecord} />
            <div>
                {showPath && (
                    <div>
                        <FilesBreadcrumbs
                            size="small"
                            onLevelSelect={onBreadCrumbsClick}
                            path={record.path.slice(0, record.path.length - 1)}
                        />
                    </div>
                )}
                <p className="text-sm text-left font-medium">{record.name}</p>
            </div>
        </div>
    );
}

export default MoveFileWindow;
